import React, { useMemo, useRef, useState } from "react";
import rinkImg from "./rink.png";

const WEIGHTS = {
  arenaAdjustedShotDistance: 2.034786,
  xCordAdjusted: 1.013991,
  shotOnEmptyNet: 0.635251,
  shotType_SLAP: 0.17001,
  defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff: 0.145304,
  shotType_BACK: 0.144711,
  shotType_SNAP: 0.134381,
  shotAngleAdjusted: 0.133337,
  averageRestDifference: 0.126043,
  shotType_TIP: 0.107933,
  shotType_WRAP: 0.09564,
  yCordAdjusted: 0.06069,
  offWing: 0.058713,
  timeSinceLastEvent: 0.052115,
  shotRebound: 0.050707,
  period: 0.046885,
  shotType_DEFL: 0.041758,
  isHomeTeam: 0.019055,
  ourGoaliePulled: 0.013362,
  defendingTeamAverageTimeOnIceOfDefencemenSinceFaceoff: 0.012699,
};

const SHOT_TYPES = [
  { value: "WRIST", label: "Wrist" },
  { value: "SNAP", label: "Snap" },
  { value: "SLAP", label: "Slap" },
  { value: "BACK", label: "Backhand" },
  { value: "TIP", label: "Tip" },
  { value: "DEFL", label: "Deflection" },
  { value: "WRAP", label: "Wraparound" },
];

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function sigmoid(x) {
  return 1 / (1 + Math.exp(-x));
}

function normalize(value, min, max) {
  if (max === min) return 0;
  return (value - min) / (max - min);
}

function getDistanceAndAngle(x, y, width, height) {
  const goalX = width / 2;
  const goalY = height - 18;

  const dx = x - goalX;
  const dy = goalY - y;

  const distance = Math.sqrt(dx * dx + dy * dy);
  const angleRadians = Math.atan2(Math.abs(dx), Math.max(dy, 1));
  const angleDegrees = (angleRadians * 180) / Math.PI;

  return { distance, angleDegrees };
}

function getShotTypeScore(shotType) {
  switch (shotType) {
    case "SLAP":
      return WEIGHTS.shotType_SLAP;
    case "BACK":
      return WEIGHTS.shotType_BACK;
    case "SNAP":
      return WEIGHTS.shotType_SNAP;
    case "TIP":
      return WEIGHTS.shotType_TIP;
    case "WRAP":
      return WEIGHTS.shotType_WRAP;
    case "DEFL":
      return WEIGHTS.shotType_DEFL;
    default:
      return 0;
  }
}

function getOutcomeMessage(isGoal, probability) {
  if (isGoal) {
    if (probability >= 0.15) return "Goal! High-danger chance finished.";
    if (probability >= 0.1) return "Goal! Clean release beats the goalie.";
    return "Goal! It found a way in.";
  }

  if (probability >= 0.15) return "Saved. Great chance, but the goalie had it.";
  if (probability >= 0.1) return "Missed. Decent look, but no finish.";
  if (probability >= 0.06) return "Blocked before it reached the net.";
  return "Low-danger attempt. No goal.";
}

export default function Model() {
  const RINK_WIDTH = 320;
  const RINK_HEIGHT = 480;

  const rinkRef = useRef(null);

  const [shotPoint, setShotPoint] = useState({ x: 160, y: 130 });
  const [shotType, setShotType] = useState("WRIST");
  const [offWing, setOffWing] = useState(0);
  const [shotRebound, setShotRebound] = useState(0);
  const [period, setPeriod] = useState(2);
  const [averageRestDifference, setAverageRestDifference] = useState(0);
  const [timeSinceLastEvent, setTimeSinceLastEvent] = useState(2.5);
  const [isHomeTeam, setIsHomeTeam] = useState(1);
  const [ourGoaliePulled, setOurGoaliePulled] = useState(0);
  const [shotOnEmptyNet, setShotOnEmptyNet] = useState(0);
  const [defForwardsSinceFaceoff, setDefForwardsSinceFaceoff] = useState(20);
  const [defDefencemenSinceFaceoff, setDefDefencemenSinceFaceoff] = useState(20);

  const [attempts, setAttempts] = useState(0);
  const [goals, setGoals] = useState(0);
  const [resultState, setResultState] = useState("idle");
  const [resultText, setResultText] = useState("Click on the ice, adjust the sliders, and fire a shot.");

  const { distance, angleDegrees } = useMemo(() => {
    return getDistanceAndAngle(
      shotPoint.x,
      shotPoint.y,
      RINK_WIDTH,
      RINK_HEIGHT
    );
  }, [shotPoint]);

  const probability = useMemo(() => {
    // Feature shaping:
    // Since the values you sent appear to be importances rather than signed coefficients,
    // this approximates a scoring model by rewarding plausible goal-friendly conditions.

    const distanceScore = 1 - normalize(clamp(distance, 0, 260), 0, 260);
    const centerLineDistance = Math.abs(shotPoint.x - RINK_WIDTH / 2);
    const xScore = 1 - normalize(centerLineDistance, 0, RINK_WIDTH / 2);
    const yScore = normalize(shotPoint.y, 0, RINK_HEIGHT);
    const angleScore = 1 - normalize(clamp(angleDegrees, 0, 90), 0, 90);
    const restScore = normalize(clamp(averageRestDifference, -40, 40), -40, 40);
    const timeScore = 1 - normalize(clamp(timeSinceLastEvent, 0, 10), 0, 10);
    const defForwardScore = normalize(clamp(defForwardsSinceFaceoff, 0, 45), 0, 45);
    const defDefencemenScore = normalize(clamp(defDefencemenSinceFaceoff, 0, 45), 0, 45);
    const periodScore = normalize(clamp(period, 1, 3), 1, 3);
    const shotTypeScore = getShotTypeScore(shotType);

    const weightedRaw =
      distanceScore * WEIGHTS.arenaAdjustedShotDistance +
      xScore * WEIGHTS.xCordAdjusted +
      shotOnEmptyNet * WEIGHTS.shotOnEmptyNet +
      shotTypeScore +
      defForwardScore * WEIGHTS.defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff +
      angleScore * WEIGHTS.shotAngleAdjusted +
      restScore * WEIGHTS.averageRestDifference +
      yScore * WEIGHTS.yCordAdjusted +
      offWing * WEIGHTS.offWing +
      timeScore * WEIGHTS.timeSinceLastEvent +
      shotRebound * WEIGHTS.shotRebound +
      periodScore * WEIGHTS.period +
      isHomeTeam * WEIGHTS.isHomeTeam +
      ourGoaliePulled * WEIGHTS.ourGoaliePulled +
      defDefencemenScore * WEIGHTS.defendingTeamAverageTimeOnIceOfDefencemenSinceFaceoff;

    // Scale weighted score into a believable hockey shooting range.
    const probabilityEstimate = sigmoid(-3.15 + weightedRaw * 0.92);

    return clamp(probabilityEstimate, 0.01, 0.45);
  }, [
    distance,
    angleDegrees,
    shotPoint.x,
    shotPoint.y,
    shotType,
    offWing,
    shotRebound,
    period,
    averageRestDifference,
    timeSinceLastEvent,
    isHomeTeam,
    ourGoaliePulled,
    shotOnEmptyNet,
    defForwardsSinceFaceoff,
    defDefencemenSinceFaceoff,
  ]);

  const probabilityPercent = (probability * 100).toFixed(1);

  const handleRinkClick = (e) => {
    const rect = rinkRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = ((e.clientX - rect.left) / rect.width) * RINK_WIDTH;
    const clickY = ((e.clientY - rect.top) / rect.height) * RINK_HEIGHT;

    setShotPoint({
      x: clamp(clickX, 10, RINK_WIDTH - 10),
      y: clamp(clickY, 10, RINK_HEIGHT - 10),
    });
  };

  const handleShoot = () => {
    const didScore = Math.random() < probability;

    setAttempts((prev) => prev + 1);

    if (didScore) {
      setGoals((prev) => prev + 1);
      setResultState("goal");
    } else {
      setResultState("miss");
    }

    setResultText(getOutcomeMessage(didScore, probability));
  };

  const handleReset = () => {
    setAttempts(0);
    setGoals(0);
    setResultState("idle");
    setResultText("Click on the ice, adjust the sliders, and fire a shot.");
  };

  const ringColor =
    resultState === "goal"
      ? "0 0 0 4px rgba(34, 197, 94, 0.28)"
      : resultState === "miss"
      ? "0 0 0 4px rgba(239, 68, 68, 0.24)"
      : "0 0 0 1px rgba(148, 163, 184, 0.25)";

  const borderColor =
    resultState === "goal"
      ? "#22c55e"
      : resultState === "miss"
      ? "#ef4444"
      : "#cbd5e1";

  return (
    <div style={styles.page}>
      <div data-model-root style={{ ...styles.container, gridTemplateColumns: window.innerWidth < 900 ? "1fr" : "1.2fr 0.8fr" }}>
        <div style={styles.leftCard}>
          <div style={styles.headerBlock}>
            <h1 style={styles.title}>Shot Success Model</h1>
            <p style={styles.subtitle}>
              Click a shot location on the rink, adjust the context, and press Shoot
              to simulate whether the puck goes in.
            </p>
          </div>

          <div
            style={{
              ...styles.rinkOuter,
              borderColor,
              boxShadow: ringColor,
            }}
          >
            <div
              ref={rinkRef}
              style={styles.rinkInner}
              onClick={handleRinkClick}
            >
              <img
                src={rinkImg}
                alt="Half ice hockey rink"
                style={styles.rinkImage}
                draggable={false}
              />

              <div
                style={{
                  ...styles.markerWrap,
                  left: `${(shotPoint.x / RINK_WIDTH) * 100}%`,
                  top: `${(shotPoint.y / RINK_HEIGHT) * 100}%`,
                }}
              >
                <div style={styles.markerPulse} />
                <div style={styles.markerDot} />
              </div>
            </div>
          </div>

       <div
  style={{
    ...styles.statsGrid,
    gridTemplateColumns: window.innerWidth < 700 ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
  }}
>
            <StatCard label="Goal Probability" value={`${probabilityPercent}%`} />
            
            <StatCard label="Angle" value={`${angleDegrees.toFixed(1)}°`} />
            <StatCard label="Results" value={`${goals}/${attempts}`} />
          </div>

          <div style={styles.resultBanner}>{resultText}</div>

          <div style={styles.buttonRow}>
            <button style={styles.shootButton} onClick={handleShoot}>
              Shoot
            </button>
            <button style={styles.resetButton} onClick={handleReset}>
              Reset Results
            </button>
          </div>
        </div>

        <div style={styles.rightCard}>
          <h2 style={styles.panelTitle}>Shot Context</h2>

          <div style={styles.fieldBlock}>
            <label style={styles.label}>Shot Type</label>
            <select
              value={shotType}
              onChange={(e) => setShotType(e.target.value)}
              style={styles.select}
            >
              {SHOT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <SliderRow
            label="Average Rest Difference"
            value={averageRestDifference}
            min={-40}
            max={40}
            step={0.5}
            onChange={setAverageRestDifference}
          />

          <SliderRow
            label="Time Since Last Event"
            value={timeSinceLastEvent}
            min={0}
            max={10}
            step={0.1}
            onChange={setTimeSinceLastEvent}
          />

          <SliderRow
            label="Def. Forwards TOI Since Faceoff"
            value={defForwardsSinceFaceoff}
            min={0}
            max={45}
            step={0.5}
            onChange={setDefForwardsSinceFaceoff}
          />

          <SliderRow
            label="Def. Defencemen TOI Since Faceoff"
            value={defDefencemenSinceFaceoff}
            min={0}
            max={45}
            step={0.5}
            onChange={setDefDefencemenSinceFaceoff}
          />

          <SliderRow
            label="Period"
            value={period}
            min={1}
            max={3}
            step={1}
            onChange={setPeriod}
          />

          <ToggleRow
            label="Rebound"
            checked={!!shotRebound}
            onChange={() => setShotRebound((prev) => (prev ? 0 : 1))}
          />

          <ToggleRow
            label="Off Wing"
            checked={!!offWing}
            onChange={() => setOffWing((prev) => (prev ? 0 : 1))}
          />

          <ToggleRow
            label="Home Team"
            checked={!!isHomeTeam}
            onChange={() => setIsHomeTeam((prev) => (prev ? 0 : 1))}
          />

          <ToggleRow
            label="Goalie Pulled"
            checked={!!ourGoaliePulled}
            onChange={() => setOurGoaliePulled((prev) => (prev ? 0 : 1))}
          />

          <ToggleRow
            label="Empty Net"
            checked={!!shotOnEmptyNet}
            onChange={() => setShotOnEmptyNet((prev) => (prev ? 0 : 1))}
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange }) {
  return (
    <div style={styles.fieldBlock}>
      <div style={styles.sliderHeader}>
        <label style={styles.label}>{label}</label>
        <span style={styles.sliderValue}>{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={styles.range}
      />
      <div style={styles.rangeEnds}>
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        ...styles.toggleRow,
        ...(checked ? styles.toggleRowOn : styles.toggleRowOff),
      }}
    >
      <span style={styles.toggleText}>{label}</span>
      <span
        style={{
          ...styles.toggleBadge,
          ...(checked ? styles.toggleBadgeOn : styles.toggleBadgeOff),
        }}
      >
        {checked ? "On" : "Off"}
      </span>
    </button>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    padding: "16px 12px",
    fontFamily: "Arial, sans-serif",
  },
  container: {
    maxWidth: "1180px",
    margin: "0 auto",
    display: "grid",
    gap: "24px",
    gridTemplateColumns: "1.2fr 0.8fr",
  },
  leftCard: {
    background: "#ffffff",
    borderRadius: "24px",
    padding: "18px",
    boxShadow: "0 4px 18px rgba(15, 23, 42, 0.06)",
    border: "1px solid #e2e8f0",
  },
  rightCard: {
    background: "#ffffff",
    borderRadius: "24px",
    padding: "18px",
    boxShadow: "0 4px 18px rgba(15, 23, 42, 0.06)",
    border: "1px solid #e2e8f0",
  },
  headerBlock: {
    marginBottom: "16px",
  },
  title: {
    margin: 0,
    fontSize: "30px",
    lineHeight: 1.1,
    color: "#0f172a",
  },
  subtitle: {
    marginTop: "10px",
    marginBottom: 0,
    color: "#475569",
    fontSize: "15px",
    lineHeight: 1.5,
  },
  rinkOuter: {
    borderRadius: "24px",
    overflow: "hidden",
    border: "4px solid #cbd5e1",
    transition: "all 0.25s ease",
    background: "#ffffff",
  },
rinkInner: {
  position: "relative",
  width: "100%",
  height: "auto",
  maxWidth: "300px", // match actual image width
  margin: "0 auto",
  aspectRatio: "300 / 300",
  cursor: "crosshair",
  userSelect: "none",
},
  rinkImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },
  markerWrap: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    width: "22px",
    height: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  markerPulse: {
    position: "absolute",
    width: "22px",
    height: "22px",
    borderRadius: "999px",
    background: "rgba(37, 99, 235, 0.22)",
  },
  markerDot: {
    position: "relative",
    width: "12px",
    height: "12px",
    borderRadius: "999px",
    background: "#2563eb",
    border: "2px solid white",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.25)",
  },
  statsGrid: {
    display: "grid",
    gap: "12px",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    marginTop: "16px",
  },
  statCard: {
    background: "#f1f5f9",
    borderRadius: "18px",
    padding: "14px",
  },
  statLabel: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#64748b",
  },
  statValue: {
    marginTop: "6px",
    fontSize: "24px",
    fontWeight: 700,
    color: "#0f172a",
  },
  resultBanner: {
    marginTop: "16px",
    background: "#0f172a",
    color: "white",
    borderRadius: "18px",
    padding: "14px 16px",
    fontSize: "15px",
    lineHeight: 1.45,
  },
  buttonRow: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
    flexWrap: "wrap",
  },
  shootButton: {
    flex: 1,
    minWidth: "180px",
    border: "none",
    borderRadius: "18px",
    background: "#2563eb",
    color: "white",
    fontSize: "20px",
    fontWeight: 700,
    padding: "16px 18px",
    cursor: "pointer",
  },
  resetButton: {
    flex: 1,
    minWidth: "180px",
    border: "1px solid #cbd5e1",
    borderRadius: "18px",
    background: "white",
    color: "#334155",
    fontSize: "18px",
    fontWeight: 600,
    padding: "16px 18px",
    cursor: "pointer",
  },
  panelTitle: {
    marginTop: 0,
    marginBottom: "16px",
    fontSize: "24px",
    color: "#0f172a",
  },
  fieldBlock: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#334155",
  },
  select: {
    width: "100%",
    borderRadius: "14px",
    border: "1px solid #cbd5e1",
    padding: "12px 14px",
    fontSize: "15px",
    background: "white",
    color: "#0f172a",
  },
  sliderHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
  },
  sliderValue: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#0f172a",
  },
  range: {
    width: "100%",
    marginTop: "8px",
  },
  rangeEnds: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "4px",
    fontSize: "12px",
    color: "#94a3b8",
  },
  toggleRow: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "18px",
    padding: "14px 16px",
    marginBottom: "12px",
    cursor: "pointer",
    border: "1px solid #cbd5e1",
    background: "white",
  },
  toggleRowOn: {
    border: "1px solid #2563eb",
    background: "#eff6ff",
  },
  toggleRowOff: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
  },
  toggleText: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#1e293b",
  },
  toggleBadge: {
    borderRadius: "999px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 700,
  },
  toggleBadgeOn: {
    background: "#2563eb",
    color: "#ffffff",
  },
  toggleBadgeOff: {
    background: "#f1f5f9",
    color: "#475569",
  },
};

if (typeof window !== "undefined") {
  const applyMobileStyles = () => {
    const isMobile = window.innerWidth < 900;
    const root = document.documentElement;

    if (isMobile) {
      root.style.setProperty("--model-grid-columns", "1fr");
    } else {
      root.style.setProperty("--model-grid-columns", "1.2fr 0.8fr");
    }
  };

  setTimeout(() => {
    const container = document.querySelector("[data-model-root]");
    if (container) applyMobileStyles();
  }, 0);
}